/**
 * №16.1.4 — authoring humanisation: the shared LOOKUP components. One source for
 * "declared, queryable subject fields" (the D-RS6 reporting-attributes registry =
 * the template's subject entity type) + typed value inputs + role/agent pickers.
 * Every editor (effects, conditions, the step Action native-op form, assignee)
 * reads these — so no author ever types an internal string, and no picker sources
 * its options from a literal (R5: options come from the registry, never hardcoded).
 */
import { useListTree } from "../../../lib/hooks/use-kuvio";
import { useAuth } from "../../../lib/hooks/use-auth";
import { useRoles } from "../../hooks/use-policy";
import { fieldLabel, useSubjectFields } from "./wf-fields-shared";

const selStyle = { width: "100%" } as const;

/** Pick a declared field of the subject entity (optionally date-typed only). */
export function AttributeSelect({ subjectType, value, onChange, busy, dateOnly, placeholder }: {
  subjectType: string | undefined; value: string; onChange: (v: string) => void; busy?: boolean; dateOnly?: boolean; placeholder?: string;
}) {
  const { fields, resolved } = useSubjectFields(subjectType);
  const opts = dateOnly ? fields.filter((f) => f.type === "date" || f.type === "datetime") : fields;
  // Honesty: if the picked field is no longer declared (or the registry hasn't
  // resolved), keep it selectable so we never silently drop an authored value.
  const known = opts.some((f) => f.name === value);
  return (
    <select value={value} disabled={busy} onChange={(e) => onChange(e.target.value)} style={selStyle}>
      <option value="">{placeholder ?? (dateOnly ? "— pick a date field —" : "— pick a field —")}</option>
      {opts.map((f) => <option key={f.name} value={f.name}>{fieldLabel(f.name)}</option>)}
      {value && !known && <option value={value}>{fieldLabel(value)}{resolved ? " (not declared)" : ""}</option>}
    </select>
  );
}

/**
 * A value input that TYPES ITSELF from the picked field: closed enum → option
 * dropdown; open option-set → suggest-but-accept (datalist); date/number → the
 * native typed control; free-text only when the field is free-text (or unknown).
 */
export function TypedValueInput({ subjectType, fieldName, value, onChange, busy, placeholder }: {
  subjectType: string | undefined; fieldName: string; value: string; onChange: (v: string) => void; busy?: boolean; placeholder?: string;
}) {
  const { byName } = useSubjectFields(subjectType);
  const f = fieldName ? byName.get(fieldName) : undefined;
  const choices = f?.choices ?? [];

  if (f && f.type === "enum" && choices.length) {
    if (f.option_set_open) {
      const listId = `wfvo-${subjectType}-${fieldName}`;
      return (
        <>
          <input list={listId} value={value} disabled={busy} onChange={(e) => onChange(e.target.value)} placeholder={placeholder ?? "value"} style={selStyle} />
          <datalist id={listId}>{choices.map((c) => <option key={c} value={c} />)}</datalist>
        </>
      );
    }
    const known = choices.includes(value);
    return (
      <select value={value} disabled={busy} onChange={(e) => onChange(e.target.value)} style={selStyle}>
        <option value="">— pick a value —</option>
        {choices.map((c) => <option key={c} value={c}>{c}</option>)}
        {value && !known && <option value={value}>{value} (not an option)</option>}
      </select>
    );
  }
  if (f && f.type === "date") return <input type="date" value={value} disabled={busy} onChange={(e) => onChange(e.target.value)} style={selStyle} />;
  if (f && f.type === "datetime") return <input type="datetime-local" value={value} disabled={busy} onChange={(e) => onChange(e.target.value)} style={selStyle} />;
  if (f && (f.type === "number" || f.type === "currency")) return <input type="number" value={value} disabled={busy} onChange={(e) => onChange(e.target.value)} style={selStyle} />;
  return <input type="text" value={value} disabled={busy} onChange={(e) => onChange(e.target.value)} placeholder={placeholder ?? "value"} style={selStyle} />;
}

/** Pick a status the subject entity actually declares. */
export function StatusSelect({ subjectType, value, onChange, busy }: {
  subjectType: string | undefined; value: string; onChange: (v: string) => void; busy?: boolean;
}) {
  const { statusChoices } = useSubjectFields(subjectType);
  if (!statusChoices.length) return <input type="text" value={value} disabled={busy} onChange={(e) => onChange(e.target.value)} placeholder="status" style={selStyle} />;
  const known = statusChoices.includes(value);
  return (
    <select value={value} disabled={busy} onChange={(e) => onChange(e.target.value)} style={selStyle}>
      <option value="">— pick a status —</option>
      {statusChoices.map((c) => <option key={c} value={c}>{c}</option>)}
      {value && !known && <option value={value}>{value} (not an option)</option>}
    </select>
  );
}

/** Pick a workspace role (№11.2) — the assignee role slots, by human name. */
export function WorkspaceRoleSelect({ value, onChange, busy }: { value: string; onChange: (v: string) => void; busy?: boolean }) {
  const roles = useRoles();
  const list = roles.data ?? [];
  const known = list.some((r) => r.name === value);
  return (
    <select value={value} disabled={busy} onChange={(e) => onChange(e.target.value)} style={selStyle}>
      <option value="">— pick a role —</option>
      {list.map((r) => <option key={r.name} value={r.name}>{fieldLabel(r.name)}</option>)}
      {value && !known && <option value={value}>{fieldLabel(value)}</option>}
    </select>
  );
}

/** Pick an agent (a №15 roster teammate) by name — never a raw node id. */
export function AgentSelect({ value, onChange, busy }: { value: string; onChange: (v: string) => void; busy?: boolean }) {
  const { currentWorkspaceId } = useAuth();
  const tree = useListTree(currentWorkspaceId ?? undefined);
  const agents = (tree.data ?? []).filter((n) => n.type === "agent");
  const known = agents.some((a) => a.id === value);
  return (
    <select value={value} disabled={busy} onChange={(e) => onChange(e.target.value)} style={selStyle}>
      <option value="">— pick an agent —</option>
      {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      {value && !known && <option value={value}>{value}</option>}
    </select>
  );
}
